import React, { useState } from 'react';
import {
  X, Calendar, Clock, Users, Building2,
  Video, MessageSquare, CheckCircle2, AlertCircle
} from 'lucide-react';
import { employeeData } from '../data/mockData';
import ModalWrapper from '../../../components/ModalWrapper';

const ScheduleInterviewForm = ({ candidate, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    type: 'technical',
    date: '',
    time: '',
    duration: '60',
    interviewers: [''],
    location: 'office',
    meetingLink: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const interviewTypes = [
    { value: 'technical', label: 'Technical Interview' },
    { value: 'hr', label: 'HR Interview' },
    { value: 'culture', label: 'Culture Fit' },
    { value: 'final', label: 'Final Round' }
  ];

  const durations = [
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInterviewerChange = (index, value) => {
    const newInterviewers = [...formData.interviewers];
    newInterviewers[index] = value;
    setFormData(prev => ({
      ...prev,
      interviewers: newInterviewers
    }));
  };

  const addInterviewer = () => {
    setFormData(prev => ({
      ...prev,
      interviewers: [...prev.interviewers, '']
    }));
  };

  const removeInterviewer = (index) => {
    setFormData(prev => ({
      ...prev,
      interviewers: prev.interviewers.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.interviewers[0]) newErrors.interviewers = 'At least one interviewer is required';
    if (formData.location === 'remote' && !formData.meetingLink) {
      newErrors.meetingLink = 'Meeting link is required for remote interviews';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const interviewData = {
        ...formData,
        candidateId: candidate.id,
        candidateName: candidate.name,
        interviewers: formData.interviewers.filter(interviewer => interviewer.trim()),
        status: 'scheduled'
      };

      onSubmit(interviewData);
      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ submit: 'Failed to schedule interview. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper isOpen={true} onClose={onClose}>
      {/* Header */}
      <header className="p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Schedule Interview</h2>
            <p className="text-sm text-gray-500">with {candidate.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Body */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Interview Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Interview Type *
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            {interviewTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                errors.date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-500">{errors.date}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time *
            </label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                errors.time ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.time && (
              <p className="mt-1 text-sm text-red-500">{errors.time}</p>
            )}
          </div>
        </div>

        {/* Duration and Location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration
            </label>
            <select
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {durations.map((duration) => (
                <option key={duration.value} value={duration.value}>
                  {duration.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <select
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="office">Office</option>
              <option value="remote">Remote</option>
            </select>
          </div>
        </div>

        {/* Meeting Link (for remote interviews) */}
        {formData.location === 'remote' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Link *
            </label>
            <input
              type="url"
              name="meetingLink"
              value={formData.meetingLink}
              onChange={handleChange}
              placeholder="https://meet.example.com/..."
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                errors.meetingLink ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.meetingLink && (
              <p className="mt-1 text-sm text-red-500">{errors.meetingLink}</p>
            )}
          </div>
        )}

        {/* Interviewers */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">
              Interviewers *
            </label>
            <button
              type="button"
              onClick={addInterviewer}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              Add Interviewer
            </button>
          </div>
          {formData.interviewers.map((interviewer, index) => (
            <div key={index} className="flex gap-2">
              <select
                value={interviewer}
                onChange={(e) => handleInterviewerChange(index, e.target.value)}
                className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  errors.interviewers && index === 0 ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Interviewer</option>
                {employeeData.employees.map((emp) => (
                  <option key={emp.id} value={emp.name}>
                    {emp.name} - {emp.position}
                  </option>
                ))}
              </select>
              {formData.interviewers.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeInterviewer(index)}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
          {errors.interviewers && (
            <p className="mt-1 text-sm text-red-500">{errors.interviewers}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Any special instructions or agenda points..."
          />
        </div>

        {/* Submit Status */}
        {submitStatus && (
          <div className={`p-4 rounded-lg ${
            submitStatus === 'success' 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {submitStatus === 'success' ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span>
                {submitStatus === 'success'
                  ? 'Interview scheduled successfully!'
                  : 'Failed to schedule interview. Please try again.'}
              </span>
            </div>
          </div>
        )}
      </form>

      {/* Footer */}
      <footer className="p-6 bg-gray-50">
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            onClick={handleSubmit}
            className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
              transition-colors flex items-center gap-2 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Scheduling...</span>
              </>
            ) : (
              'Schedule Interview'
            )}
          </button>
        </div>
      </footer>
    </ModalWrapper>
  );
};

export default ScheduleInterviewForm; 